import type {
  ChoiceListMinimal,
  FormulaID,
  FormulaOutputType,
  MixComponent,
  OutputID,
  PotentialEmissionsBasisPeriod,
  ReleaseProfileChemicalProfile,
  ReleaseProfileFormula,
  ReleaseProfileFormulaChoiceID,
  ReleaseProfileFormulaChoiceListID,
  ReleaseProfileMixCalculation,
  ReleaseProfileMixCalculationDefinition,
  ReleaseProfileQuestionDefinition,
  ReleaseProfilesAndVariables,
  ReleaseProfileWasteStream,
  SubFormulaBrief,
  VarNameToUsageMixResults,
} from '../types/release-profiles.ts';
import { mixComponentToQuestionID } from './questionnaires.ts';
import type {
  FacilityProductBrief,
  ReleaseProfileAnswerValues,
  ReleaseProfileBrief,
  ReleaseProfileProcessWithChildren,
  ThresholdBrief,
  Warning,
} from '../types/tri.ts';
import type {
  CasOrCategory,
  ReleaseProfile,
  ReleaseProfileAttestationState,
  ReleaseProfileID,
  UsageTypeID,
} from '../types/db.ts';
import { VariableNameFinder } from './formulas/variable-name-finder.ts';
import { formulaStringImpliesProduct } from './formulas/formula-utils.ts';
import { type FormRLeafLine } from './form-r/form-r-sections.ts';
import type { ReleaseComputed } from './releases-calculate.ts';
import type { QuestionnaireAnswers, QuestionnaireFormulaChoicesContainer } from '../types/questionnaires.ts';
import type { TRIQuantityBasisEstimationCodeType } from '../types/form-r.ts';
import { setIntersection } from '../../cross-app-shared/services/util.ts';
import { VariableNameReplacer } from './formulas/variable-name-replacer.ts';
import type { Expression } from './formulas/ast.ts';
import type { Section4ProductInfo } from './form-r/form-r-maximum-chemical-amount-code.ts';
import { UsageTypeIds } from './usage-types.ts';

/** Resolve a Tier II question without leaking facility-scoped values into product scope. */
export function releaseProfileQuestionAnswerForTierII(
  definition: Pick<ReleaseProfileQuestionDefinition, 'questionID' | 'differsPerProductInTierII'>,
  facilityAnswers: ReleaseProfileAnswerValues,
  productAnswers: ReleaseProfileAnswerValues | undefined,
): number | undefined {
  return definition.differsPerProductInTierII
    ? productAnswers?.[definition.questionID]
    : facilityAnswers[definition.questionID];
}

export function releaseProfileComputeMixResults(
  mixCalculation: Pick<ReleaseProfileMixCalculationDefinition, 'chemicals' | 'components'>,
  componentMixPercentageGet: (component: MixComponent) => number,
): ReleaseProfileMixCalculationDefinition['results'] {
  const results: ReleaseProfileMixCalculationDefinition['results'] = {};

  for (const cas of mixCalculation.chemicals) {
    let sum = 0;
    for (const component of mixCalculation.components) {
      const concentration = component.concentrations[cas];
      if (concentration) {
        sum += (componentMixPercentageGet(component) / 100) * (concentration / 100);
      }
    }
    results[cas] = sum * 100;
  }

  return results;
}

export function releaseProfileComputeMixResultsFromStaticComponents(
  mixCalculation: Pick<ReleaseProfileMixCalculationDefinition, 'chemicals' | 'components'>,
): ReleaseProfileMixCalculationDefinition['results'] {
  return releaseProfileComputeMixResults(mixCalculation, (c) => c.percentage);
}

export function releaseProfileComputeMixResultsFromQuestionnaire(
  mixCalculation: ReleaseProfileMixCalculation,
  answers: ReleaseProfileAnswerValues,
): { results: ReleaseProfileMixCalculationDefinition['results']; warnings: Warning[] } {
  const warnings: Warning[] = [];
  return {
    results: releaseProfileComputeMixResults(mixCalculation.definition, (c) => {
      const questionID = mixComponentToQuestionID(c);
      const answer = answers[questionID];
      if (answer === undefined) {
        warnings.push({
          warningID: 'TW1037',
          type: 'questionnaire',
          subType: questionID,
          casOrCategory: null,
          message: `Expected to find mix percentage for component "${c.name}" in mix "${mixCalculation.nameFriendly}"`,
          blocksPriorYearSection8Calculation: true,
        });
        return 0;
      } else {
        return answer;
      }
    }),
    warnings,
  };
}

export function* releaseProfilesIterateFormulas(formula: ReleaseProfile['formula']): Generator<string, void, unknown> {
  for (const { formula: formulaStr } of Object.values(formula.formulas)) {
    yield formulaStr;
  }
}

export function* releaseProfilesIterateChemicals(
  profilesAndVariables: ReleaseProfilesAndVariables,
  usageMixResults: VarNameToUsageMixResults,
  releaseProfileID: ReleaseProfileID,
): Generator<CasOrCategory, void, unknown> {
  const found = profilesAndVariables.releaseProfiles.find((p) => p.releaseProfileID === releaseProfileID);
  if (!found) {
    return;
  }

  if (found.formula.type === 'formulaElaborated') {
    for (const { outputs } of Object.values(found.formula.formulas)) {
      for (const { chemicals } of Object.values(outputs)) {
        if (Array.isArray(chemicals)) {
          yield* chemicals;
        }
        // 'allVolatile' and 'allSolid' are intentionally not enumerated here because they
        // require product-level data to resolve. These modes are only allowed on formulas that
        // use product concentration variables, so the chemicals are already captured through
        // the product assignment on the process/emissions unit.
      }
    }
  }

  for (const formula of releaseProfilesIterateFormulas(found.formula)) {
    const variables = VariableNameFinder.findInStringIgnoringErrors(formula);

    for (const nameForVariable of variables) {
      const chemicalProfile = profilesAndVariables.chemicalProfiles.find((p) => p.nameForVariable === nameForVariable);
      if (chemicalProfile) {
        for (const { casOrCategory } of chemicalProfile.definition.chemicals) {
          yield casOrCategory;
        }
        continue;
      }

      const mixCalculation = profilesAndVariables.mixCalculations.find((m) => m.nameForVariable === nameForVariable);
      if (mixCalculation) {
        yield* mixCalculation.definition.chemicals;
        continue;
      }

      const usageMix = profilesAndVariables.usageMixes.find((u) => u.nameForVariable === nameForVariable);
      if (usageMix && usageMixResults[nameForVariable]) {
        yield* Object.keys(usageMixResults[nameForVariable]!.percentages) as CasOrCategory[];
      }
    }
  }
}

export function releaseProfilesAndVariablesEmpty(): ReleaseProfilesAndVariables {
  return {
    releaseProfiles: [],
    constants: [],
    questions: [],
    chemicalProfiles: [],
    mixCalculations: [],
    usageMixes: [],
    wasteStreams: [],
  };
}

export interface ProductsAndUsageTypesByCasOrCategory {
  casOrCategory: CasOrCategory;
  products: FacilityProductBrief[];
  usageTypes: Set<UsageTypeID>;
  usageTypeSources: Record<
    UsageTypeID,
    {
      products: FacilityProductBrief[];
      releaseProfiles: ReleaseProfileBrief[];
      chemicalProfiles: ReleaseProfileChemicalProfile[];
    }
  >;
}

/**
 * Extracts usage types from thresholds and chemical profiles.
 * The output of this function determines which chemicals `appDataToFormRXML` ultimately includes in its report.
 */
export function productsAndUsageTypesByCasOrCategory({
  thresholds,
  productUsage,
  releaseProfiles,
  chemicalProfiles,
  processes,
}: {
  thresholds: ThresholdBrief[];
  productUsage: FacilityProductBrief[];
  releaseProfiles: ReleaseProfileBrief[];
  chemicalProfiles: ReleaseProfileChemicalProfile[];
  processes: ReleaseProfileProcessWithChildren[];
}): ProductsAndUsageTypesByCasOrCategory[] {
  const results = new Map<CasOrCategory, ProductsAndUsageTypesByCasOrCategory>();
  const releaseProfilesById = new Map(releaseProfiles.map((p) => [p.releaseProfileID, p]));

  for (const threshold of thresholds) {
    if (!threshold.isIncludedInFormRReport) {
      continue;
    }

    if (!results.has(threshold.casOrCategory)) {
      const usageTypes = new Set<UsageTypeID>();
      const products: FacilityProductBrief[] = [];
      const usageTypeSources: ProductsAndUsageTypesByCasOrCategory['usageTypeSources'] = {
        [UsageTypeIds.Manufactured]: { products: [], releaseProfiles: [], chemicalProfiles: [] },
        [UsageTypeIds.Processed]: { products: [], releaseProfiles: [], chemicalProfiles: [] },
        [UsageTypeIds['Otherwise Used']]: { products: [], releaseProfiles: [], chemicalProfiles: [] },
      };

      for (const product of productUsage) {
        if (product.totalPoundsUsed <= 0) {
          continue;
        }

        let foundMatchingChemical = false;

        for (const chemical of product.chemicals) {
          if (chemical.casOrCategoryMapped === threshold.casOrCategory) {
            // If we exceed any threshold, we have to report on all uses of it, which is why we're pulling the usage
            // type from the product here and not just the threshold:
            usageTypes.add(chemical.usageTypeID);
            foundMatchingChemical = true;

            const source = usageTypeSources[chemical.usageTypeID];
            if (source && !source.products.includes(product)) {
              source.products.push(product);
            }
          }
        }

        if (foundMatchingChemical) {
          products.push(product);
        }
      }

      results.set(threshold.casOrCategory, {
        casOrCategory: threshold.casOrCategory,
        products,
        usageTypes,
        usageTypeSources,
      });
    }

    // The products probably already covered this above, but just in case:
    const item = results.get(threshold.casOrCategory)!;
    item.usageTypes.add(threshold.usageTypeID);

    // Formula-based thresholds carry calculation lines that tell us which release profiles contributed:
    for (const line of threshold.processCalculationLines) {
      if (line.thresholdUsageTypeID === null || line.releaseProfileID === null) {
        continue;
      }
      const releaseProfile = releaseProfilesById.get(line.releaseProfileID);
      if (!releaseProfile) {
        continue;
      }
      const source = item.usageTypeSources[line.thresholdUsageTypeID];
      if (source && !source.releaseProfiles.includes(releaseProfile)) {
        source.releaseProfiles.push(releaseProfile);
      }
    }
  }

  // Chemical profiles can also assign usage types, so we need to go find the ones that are missing:
  for (const chemicalProfile of chemicalProfiles) {
    for (const usageTypeID of chemicalProfile.definition.usageTypesApplied) {
      for (const { casOrCategory } of chemicalProfile.definition.chemicals) {
        const item = results.get(casOrCategory);
        if (!item) {
          continue;
        }

        // The "chemicalProfiles" array includes all possible profiles across the whole tenant, so we have to find
        // ones actually present in the formulas this facility is using:
        processLoop: for (const process of processes) {
          for (const { releaseProfileID } of process.profiles) {
            const releaseProfile = releaseProfilesById.get(releaseProfileID);
            if (!releaseProfile) {
              continue;
            }

            for (const formula of releaseProfilesIterateFormulas(releaseProfile.formula)) {
              const variableNames = VariableNameFinder.findInStringIgnoringErrors(formula);
              if (variableNames.has(chemicalProfile.nameForVariable)) {
                item.usageTypes.add(usageTypeID);

                const source = item.usageTypeSources[usageTypeID];
                if (source) {
                  if (!source.chemicalProfiles.includes(chemicalProfile)) {
                    source.chemicalProfiles.push(chemicalProfile);
                  }
                  if (!source.releaseProfiles.includes(releaseProfile)) {
                    source.releaseProfiles.push(releaseProfile);
                  }
                }

                break processLoop;
              }
            }
          }
        }
      }
    }
  }

  return Array.from(results.values());
}

export function productInfoForSection4(
  chemProducts: ProductsAndUsageTypesByCasOrCategory[],
): Map<CasOrCategory, Section4ProductInfo[]> {
  const map = new Map<CasOrCategory, Section4ProductInfo[]>();
  for (const entry of chemProducts) {
    const infos: Section4ProductInfo[] = [];
    for (const product of entry.products) {
      let concentration = 0;
      let hasMatchingChemical = false;

      for (const chemical of product.chemicals) {
        if (
          chemical.casOrCategoryMapped === entry.casOrCategory ||
          chemical.casOrCategoryOriginal === entry.casOrCategory
        ) {
          concentration += chemical.concentrationComputed;
          hasMatchingChemical = true;
        }
      }

      if (!hasMatchingChemical) {
        continue;
      }
      const poundsPerUsageUnit = product.usageUnits === 'pounds' ? 1 : (product.specificGravity ?? 1) * 8.345;
      infos.push({
        productID: product.productID,
        productName: product.name,
        concentration,
        poundsPerUsageUnit,
        totalPoundsUsed: product.totalPoundsUsed,
      });
    }
    if (infos.length > 0) {
      map.set(entry.casOrCategory, infos);
    }
  }
  return map;
}

export function releaseProfileRequiresProducts(
  profile: ReleaseProfileBrief | null | undefined,
  wasteStreams: ReleaseProfileWasteStream[],
): boolean {
  if (!profile) {
    return false;
  }

  const releaseProfileVariableNames = new Set<string>();
  for (const formula of releaseProfilesIterateFormulas(profile.formula)) {
    if (formulaStringImpliesProduct(formula)) {
      return true;
    }

    const variableNames = VariableNameFinder.findInStringIgnoringErrors(formula);
    for (const name of variableNames) {
      releaseProfileVariableNames.add(name);
    }
  }

  // Formula-based waste streams may also reference product usage:
  for (const wasteStream of wasteStreams) {
    if (wasteStream.definition.source.type !== 'formula') {
      continue;
    }

    if (!releaseProfileVariableNames.has(wasteStream.nameForVariable)) {
      continue;
    }

    if (formulaStringImpliesProduct(wasteStream.definition.source.formula)) {
      return true;
    }
  }

  return false;
}

export type AttestationStatusFromReleases = Record<
  CasOrCategory,
  {
    casOrCategory: CasOrCategory;
    releaseProfileCount: number;
    attestation: null | 'no-releases' | 'zero-releases' | 'done-adding';
  }
>;

export function releaseProfileAssignmentAttestationStatusFromReleases(
  formRLine: FormRLeafLine,
  specificAttestations: ReleaseProfileAttestationState,
  chemicalsExceedingThresholds: CasOrCategory[],
  releases: {
    chemicalsWithoutReleases: { casOrCategory: CasOrCategory; formRLine: FormRLeafLine }[];
    releases: ReleaseComputed[];
  },
): AttestationStatusFromReleases {
  const result: AttestationStatusFromReleases = {};

  for (const casOrCategory of chemicalsExceedingThresholds) {
    let attestation: null | 'no-releases' | 'zero-releases' | 'done-adding' =
      specificAttestations[casOrCategory]?.attestation || null;
    const isMissingReleases = releases.chemicalsWithoutReleases.find(
      (item) => item.casOrCategory === casOrCategory && item.formRLine === formRLine,
    );
    const releaseProfiles = new Set<ReleaseProfileID>();

    if (isMissingReleases) {
      if (attestation === 'done-adding') {
        attestation = null;
      }
    } else {
      let computationResult = 0;

      for (const release of releases.releases) {
        if (release.formRLine !== formRLine) {
          continue;
        }

        for (const chemical of release.chemicalReleases) {
          if (chemical.casOrCategory !== casOrCategory) {
            continue;
          }

          for (const transfer of chemical.transfers) {
            for (const line of transfer.calculationLines) {
              computationResult += line.computationResult;
              if (line.releaseProfileID) {
                releaseProfiles.add(line.releaseProfileID);
              }
            }
          }
        }
      }

      if ((attestation === 'no-releases' || attestation === 'zero-releases') && computationResult > 0) {
        attestation = null;
      }
    }

    result[casOrCategory] = {
      casOrCategory,
      releaseProfileCount: releaseProfiles.size,
      attestation,
    };
  }

  return result;
}

export function releaseProfileAssignmentAttestsAllChemicals(
  formRLine: FormRLeafLine,
  specificAttestations: ReleaseProfileAttestationState,
  chemicalsExceedingThresholds: CasOrCategory[],
  releases: {
    chemicalsWithoutReleases: { casOrCategory: CasOrCategory; formRLine: FormRLeafLine }[];
    releases: ReleaseComputed[];
  },
): boolean {
  const status = releaseProfileAssignmentAttestationStatusFromReleases(
    formRLine,
    specificAttestations,
    chemicalsExceedingThresholds,
    releases,
  );

  return chemicalsExceedingThresholds.every((casOrCategory) => {
    const chemicalStatus = status[casOrCategory];
    // If the user has explicitly attested, we'll consider it done. We'll also consider it done if release profiles
    // have been assigned, otherwise we end up forcing users to go back and click "Mark as Done" on every Form R line,
    // which is tedious:
    return !!chemicalStatus?.attestation || chemicalStatus?.releaseProfileCount > 0;
  });
}

// A subset of QuestionnaireMode:
type FormulaToOutputsModeBase<Acceptable> =
  | {
      type: 'triRelease';
    }
  | {
      type: 'triThreshold';
      triChemicals: Set<CasOrCategory>;
    }
  | {
      type: 'emissions';
      acceptableOutputType: Acceptable;
    }
  | {
      type: 'tier2';
      outputType: 'tier2Max' | 'tier2Avg';
    };

export type FormulaToOutputsModeStrict = FormulaToOutputsModeBase<FormulaOutputType>;

export function* formulaElaboratedToValidOutputs(
  mode: FormulaToOutputsModeBase<Set<FormulaOutputType>>,
  formulaObj: ReleaseProfileFormula,
  relevantChemicals: Set<CasOrCategory>,
  releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer,
  annotatableVariableNames: Set<string>,
): Generator<{
  formulaID: FormulaID;
  outputID: OutputID;
  nameFriendly: string;
  nameForVariable: string;
  formula: string;
  subFormulas: SubFormulaBrief[];
  ifChoices: { choiceListID: ReleaseProfileFormulaChoiceListID; choiceID: ReleaseProfileFormulaChoiceID }[];
  type: FormulaOutputType;
  chemicals: 'inferred' | 'noChemicals' | 'allVolatile' | 'allSolid' | CasOrCategory[];
  basis: '' | TRIQuantityBasisEstimationCodeType;
  potentialBasisPeriod: PotentialEmissionsBasisPeriod;
  answerIsMissingForChoice: null | {
    choiceListID: ReleaseProfileFormulaChoiceListID;
    question: string;
    answerAssumed: string;
  };
}> {
  // Profile identity also isolates empty/draft profiles and guards non-UI callers.
  if ((formulaObj.profileKind === 'inventory') !== (mode.type === 'tier2')) return;

  let acceptableOutputType: Set<FormulaOutputType>;
  if (mode.type === 'emissions') {
    acceptableOutputType = mode.acceptableOutputType;
  } else if (mode.type === 'tier2') {
    acceptableOutputType = new Set([mode.outputType]);
  } else {
    acceptableOutputType = new Set(['actual']);
  }

  for (const formula of Object.values(formulaObj.formulas)) {
    outputLoop: for (const output of Object.values(formula.outputs)) {
      if (!acceptableOutputType.has(output.type)) {
        continue;
      }

      if (output.chemicals === 'noChemicals' && mode.type !== 'emissions') {
        continue;
      }

      // If an output is only applicable to a certain set of chemicals, filter it out if none of those chemicals
      // are under consideration. 'allVolatile' and 'allSolid' are always considered applicable since the
      // actual volatile/solid filtering happens at calculation time with product chemical data.
      // Tier II inventory outputs always apply to the whole product, so they skip this filter.
      if (mode.type !== 'tier2' && Array.isArray(output.chemicals)) {
        const chemicalsForOutput = new Set(output.chemicals);
        const overlap = setIntersection(relevantChemicals, chemicalsForOutput);
        // When calculating releases, we only want the specified set of chemicals, but when we are building the
        // threshold questionnaire, we want to emit all chemical-scoped outputs as answering questions may cause us
        // to exceed a threshold:
        if (overlap.size === 0) {
          if (mode.type === 'triRelease' || mode.type === 'emissions') {
            continue;
          } else if (
            mode.type === 'triThreshold' &&
            // That said, thresholds are only relevant to TRI, so if this formula only computes emissions-relevant
            // chemicals, we can skip it:
            setIntersection(mode.triChemicals, chemicalsForOutput).size === 0
          ) {
            continue;
          }
        }
      }

      let answerIsMissingForChoice: null | {
        choiceListID: ReleaseProfileFormulaChoiceListID;
        question: string;
        answerAssumed: string;
      } = null;
      for (const { choiceListID, choiceID } of output.ifChoices) {
        // If a choice is unanswered, assume we match, but set a flag so we can warn the user.
        // This behavior is relied on by the Emissions tool to list all questions before any answers are selected.
        // This behavior is also relied on by TRI via questionsRequiredFromReleaseProfiles to list all relevant TRI
        // formulas so we can derive the relevant formula choice questions.
        if (!(choiceListID in releaseProfileFormulaChoices)) {
          answerIsMissingForChoice = {
            choiceListID,
            question: 'Unknown question',
            answerAssumed: 'Unknown answer',
          };

          const choiceList = formulaObj.choiceLists[choiceListID];
          if (choiceList) {
            answerIsMissingForChoice.question = choiceList.question;

            const choice = choiceList.choices.find((c) => c.choiceID === choiceID);
            if (choice) {
              answerIsMissingForChoice.answerAssumed = choice.title;
            }
          }
          continue;
        }

        // If choices are listed, every choice must match:
        if (releaseProfileFormulaChoices[choiceListID] !== choiceID) {
          continue outputLoop;
        }
      }

      // Formulas within a release profile can reference each other:
      const otherFormulas = new Map<string, SubFormulaBrief>();
      for (const otherFormula of Object.values(formulaObj.formulas)) {
        if (otherFormula !== formula) {
          otherFormulas.set(otherFormula.nameForVariable, otherFormula);
        }
      }

      const { formulaNew, subFormulas } = VariableNameReplacer.substituteFormulasIgnoringErrors(
        formula,
        otherFormulas,
        annotatableVariableNames,
      );
      yield {
        formulaID: formula.formulaID,
        outputID: output.outputID,
        nameFriendly: formula.nameFriendly,
        nameForVariable: formula.nameForVariable,
        formula: formulaNew,
        subFormulas,
        ifChoices: output.ifChoices,
        type: output.type,
        chemicals: output.chemicals,
        basis: output.basis,
        potentialBasisPeriod: output.potentialBasisPeriod,
        answerIsMissingForChoice,
      };
    }
  }
}

/**
 * Returns all variables names that have chemical-specific values:
 */
export function releaseProfileAnnotatableVariableNames(
  profilesAndVariables: Pick<
    ReleaseProfilesAndVariables,
    'chemicalProfiles' | 'mixCalculations' | 'usageMixes' | 'questions'
  >,
): Set<string> {
  const names = new Set<string>();
  for (const chemicalProfile of profilesAndVariables.chemicalProfiles) {
    names.add(chemicalProfile.nameForVariable);
  }
  for (const mixCalculation of profilesAndVariables.mixCalculations) {
    names.add(mixCalculation.nameForVariable);
  }
  for (const usageMix of profilesAndVariables.usageMixes) {
    names.add(usageMix.nameForVariable);
  }
  for (const question of profilesAndVariables.questions) {
    if (question.definition.isChemicalSpecific) {
      names.add(question.nameForVariable);
    }
  }
  return names;
}

/**
 * Compute the set of chemicals referenced by a given parsed formula expression:
 */
export function variablesAndChemicalsInFormulas(
  chemicalsExceedingThresholds: Set<CasOrCategory>,
  { questions, chemicalProfiles, mixCalculations, usageMixes }: ReleaseProfilesAndVariables,
  usageMixResults: VarNameToUsageMixResults,
  releaseProfileChemicalNaAnswers: QuestionnaireAnswers['releaseProfileChemicalNaAnswers'],
  expression: Expression,
) {
  const identifiers = VariableNameFinder.findIdentifiersInExpression(expression);
  const variablesReferencedAll = new Set<string>();
  const variablesReferencedWithoutAnnotations = new Set<string>();
  for (const identifier of identifiers) {
    variablesReferencedAll.add(identifier.nameBare);

    if (identifier.nameBare === identifier.nameWithAnnotation) {
      // A bare reference, without any chemical annotation, means we want to reference all chemicals that are part
      // of the given variable:
      variablesReferencedWithoutAnnotations.add(identifier.nameBare);
    } else {
      // Variables that are not referenced bare (i.e. they have some annotation like ChemicalProfile[71-43-2]) are
      // assumed to be using that value as-is and some other context in the formula will be providing the relevant
      // chemical.
    }
  }

  const questionsInFormula = questions.filter((q) => variablesReferencedAll.has(q.nameForVariable));
  const chemicalProfilesInFormula = chemicalProfiles.filter((c) => variablesReferencedAll.has(c.nameForVariable));
  const mixCalculationsInFormula = mixCalculations.filter((m) => variablesReferencedAll.has(m.nameForVariable));
  const usageMixesInFormula = usageMixes.filter((m) => variablesReferencedAll.has(m.nameForVariable));

  const chemicalsFromVariables = new Set<CasOrCategory>();

  for (const question of questionsInFormula) {
    if (question.definition.isChemicalSpecific) {
      if (variablesReferencedWithoutAnnotations.has(question.nameForVariable)) {
        for (const chemical of chemicalsExceedingThresholds) {
          chemicalsFromVariables.add(chemical);
        }
        // All chemical-specific questions add the same set of variables, so we can stop after 1 question:
        break;
      }
    }
  }

  for (const chemicalProfile of chemicalProfilesInFormula) {
    if (variablesReferencedWithoutAnnotations.has(chemicalProfile.nameForVariable)) {
      for (const chemical of chemicalProfile.definition.chemicals) {
        chemicalsFromVariables.add(chemical.casOrCategory);
      }
    }
  }

  for (const mixCalculation of mixCalculationsInFormula) {
    if (variablesReferencedWithoutAnnotations.has(mixCalculation.nameForVariable)) {
      for (const casOrCategory of mixCalculation.definition.chemicals) {
        chemicalsFromVariables.add(casOrCategory);
      }
    }
  }

  for (const usageMix of usageMixesInFormula) {
    if (variablesReferencedWithoutAnnotations.has(usageMix.nameForVariable)) {
      const chemicals = Object.keys(usageMixResults[usageMix.nameForVariable]?.percentages ?? {}) as CasOrCategory[];
      for (const casOrCategory of chemicals) {
        chemicalsFromVariables.add(casOrCategory);
      }
    }
  }

  // If a formula contains a chemical-specific question and the user provides N/A for that question, we bail out and
  // refuse to process the formula. This is made slightly more complicated by the fact that chemical-specific questions
  // can also be annotated with a specific chemical, so we must check either the chemical from the evaluation context,
  // or the chemical from the annotated variable if present:
  const mustSkipFormulaDueToNa = (casOrCategoryFromEvaluationContext: CasOrCategory) => {
    for (const question of questionsInFormula) {
      if (!question.definition.isChemicalSpecific) {
        continue;
      }

      for (const identifier of identifiers) {
        if (identifier.nameBare === question.nameForVariable) {
          const casOrCategory = identifier.annotation || casOrCategoryFromEvaluationContext;
          if (releaseProfileChemicalNaAnswers[`${question.definition.questionID}_${casOrCategory}`]) {
            return true;
          }
        }
      }
    }

    return false;
  };

  return {
    chemicalsFromVariables,
    questionsInFormula,
    mustSkipFormulaDueToNa,
    chemicalProfilesInFormula,
    mixCalculationsInFormula,
    usageMixesInFormula,
  };
}

export function releaseProfileFormulasToChoices(
  formulas: ReleaseProfileFormula[],
): Map<ReleaseProfileFormulaChoiceListID, ChoiceListMinimal> {
  const result = new Map<ReleaseProfileFormulaChoiceListID, ChoiceListMinimal>();

  for (const formula of formulas) {
    for (const choiceList of Object.values(formula.choiceLists)) {
      result.set(choiceList.choiceListID, {
        choiceListID: choiceList.choiceListID,
        question: choiceList.question,
        choices: choiceList.choices,
      });
    }
  }

  return result;
}
